import { createContext, PropsWithChildren, useEffect, useState } from 'react';
import { useEmployerInterviews } from 'hooks/useEmployerInterviews';

export const DEFAULT_DATE_RANGE = { id: 'upcoming', value: 'upcoming', label: 'Upcoming' };
export const DEFAULT_FILTERED_STATUS = { id: 'scheduled', value: 'scheduled', label: 'Scheduled' };

interface InterviewFilterData {
  dateRange: typeof DEFAULT_DATE_RANGE;
  filteredStatus: typeof DEFAULT_FILTERED_STATUS;
}
const DEFAULT_FILTERS = {
  dateRange: DEFAULT_DATE_RANGE,
  filteredStatus: DEFAULT_FILTERED_STATUS,
};

const INCLUDES = ['employer_profile', 'user_profile', 'interview_event_type'];

export type EmployerInterviewContextProps = {
  filters: typeof DEFAULT_FILTERS;
  updateFilters: (filters: Partial<InterviewFilterData>) => void;
  interviews: ReturnType<typeof useEmployerInterviews>['interviews'];
  reloadInterviews: () => void;
  loadingInterviews: ReturnType<typeof useEmployerInterviews>['loading'];
};

export const EmployerInterviewContext = createContext<EmployerInterviewContextProps>(null);

export const EmployerInterviewProvider = ({
  children,
}: PropsWithChildren<Record<string, unknown>>) => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const {
    interviews,
    fetchInterviews,
    loading: loadingInterviews,
  } = useEmployerInterviews({
    initiallyFetch: false,
  });
  const filtersPrimitive = JSON.stringify(filters);

  const getInterviews = () => {
    const initialWhere: { by_date?: Array<object>; status?: string } = {};

    if (Object.keys(filters).length > 0) {
      const where = Object.entries(filters).reduce((acc, [key, value]) => {
        if (!value.value) {
          return acc;
        }

        if (key === 'dateRange') {
          switch (value.value) {
            case 'past':
              return {
                ...acc,
                upcoming: false,
              };
            case 'upcoming':
              return {
                ...acc,
                upcoming: true,
              };
            default:
              return { ...acc, by_date: [new Date().toISOString(), value.value] };
          }
        }
        if (key === 'filteredStatus') {
          return { ...acc, status: value.value as string };
        }

        return acc;
      }, initialWhere);

      fetchInterviews({
        where,
        includes: INCLUDES,
      });
    } else {
      fetchInterviews({
        includes: INCLUDES,
      });
    }
  };

  const updateFilters = (newFilters: Partial<InterviewFilterData>) => {
    if (filtersPrimitive === JSON.stringify(newFilters)) {
      return;
    }

    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  useEffect(() => {
    getInterviews();
  }, [filtersPrimitive]);

  return (
    <EmployerInterviewContext.Provider
      value={{
        filters,
        updateFilters,
        interviews,
        reloadInterviews: getInterviews,
        loadingInterviews,
      }}
    >
      {children}
    </EmployerInterviewContext.Provider>
  );
};
